import { useState, useEffect, useRef } from 'react';
import { apiService } from '../services/api';

interface PriceData {
  ethPrice: number;
  tokenPrice: number;
  isLoading: boolean;
  error: string | null;
}

// Глобальный кеш для цен, чтобы избежать дублирования запросов между компонентами
interface PriceCache {
  ethPrice: number;
  tokenPrice: number;
  timestamp: number;
  tokenAddress?: string;
}

const CACHE_TTL = 30 * 1000; // 10 секунд кеш (для более актуальных цен)
let globalPriceCache: PriceCache | null = null;
let fetchPromise: Promise<void> | null = null;

// Функция для создания AbortController с таймаутом
const createAbortController = (timeoutMs: number): AbortController => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  controller.signal.addEventListener('abort', () => clearTimeout(timeoutId));
  return controller;
};

// Получение цены ETH через CoinGecko
async function fetchEthPrice(): Promise<number> {
  try {
    const controller = createAbortController(5000);
    const response = await fetch(
      'https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd',
      { signal: controller.signal }
    );
    
    if (!response.ok) {
      throw new Error(`CoinGecko API error: ${response.status}`);
    }
    
    const data = await response.json();
    const ethPrice = data?.ethereum?.usd;
    
    if (ethPrice && ethPrice > 0) {
      return parseFloat(ethPrice);
    }
  } catch (error) {
    console.warn('Failed to fetch ETH price from CoinGecko:', error);
  }
  
  // Fallback цена
  return 3000.0;
}

// Валидация цены токена на разумность
function validateTokenPrice(price: number, ethPrice: number): boolean {
  // Максимальная разумная цена токена - 10x от цены ETH
  const MAX_REASONABLE_PRICE = Math.max(ethPrice * 10, 10000);
  
  // Минимальная цена должна быть положительной
  if (price <= 0) {
    return false;
  }
  
  // Цена не должна быть слишком высокой (вероятно ошибка)
  if (price > MAX_REASONABLE_PRICE) {
    console.warn(`⚠️ Цена токена слишком высокая (${price}), возможно ошибка. Максимум: ${MAX_REASONABLE_PRICE}`);
    return false;
  }
  
  return true;
}

// Получение цены токена через DexScreener
async function fetchTokenPrice(tokenAddress: string, ethPrice: number = 3000): Promise<number | null> {
  if (!tokenAddress || !tokenAddress.startsWith('0x')) {
    return null;
  }

  try {
    const controller = createAbortController(5000);
    const response = await fetch(
      `https://api.dexscreener.com/latest/dex/tokens/${tokenAddress}`,
      { signal: controller.signal }
    );
    
    if (!response.ok) {
      throw new Error(`DexScreener API error: ${response.status}`);
    }
    
    const data = await response.json();
    const pairs = data?.pairs || [];
    
    if (pairs.length === 0) {
      console.warn(`⚠️ Не найдено пар для токена ${tokenAddress}`);
      return null;
    }
    
    // Находим пары на Base с минимальной ликвидностью
    const basePairs = pairs.filter((pair: any) => {
      const chainId = pair?.chainId?.toLowerCase();
      const isBase = chainId === 'base' || chainId === '8453';
      const liquidity = parseFloat(pair?.liquidity?.usd || '0');
      // Требуем минимум $1000 ликвидности для валидной пары
      return isBase && liquidity > 1000;
    });
    
    const pairsToCheck = basePairs.length > 0 ? basePairs : pairs.filter((pair: any) => {
      const liquidity = parseFloat(pair?.liquidity?.usd || '0');
      return liquidity > 1000;
    });
    
    if (pairsToCheck.length === 0) {
      console.warn(`⚠️ Не найдено пар с достаточной ликвидностью для токена ${tokenAddress}`);
      return null;
    }
    
    // Сортируем по ликвидности и берем лучшую пару
    const bestPair = pairsToCheck.sort((a: any, b: any) => {
      const liqA = parseFloat(a?.liquidity?.usd || '0');
      const liqB = parseFloat(b?.liquidity?.usd || '0');
      return liqB - liqA;
    })[0];
    
    // Пробуем разные поля для получения цены
    const priceUsdStr = bestPair?.priceUsd || bestPair?.priceNative || bestPair?.price;
    if (!priceUsdStr || priceUsdStr === 'null' || priceUsdStr === 'undefined' || priceUsdStr === null) {
      console.warn(`⚠️ Неверный формат цены от DexScreener. Данные пары:`, {
        priceUsd: bestPair?.priceUsd,
        priceNative: bestPair?.priceNative,
        price: bestPair?.price,
        pairAddress: bestPair?.pairAddress,
        chainId: bestPair?.chainId
      });
      return null;
    }
    
    let priceUsd = parseFloat(priceUsdStr);
    
    // Если priceNative используется, нужно конвертировать через цену ETH
    if (bestPair?.priceNative && !bestPair?.priceUsd) {
      const priceNative = parseFloat(bestPair.priceNative);
      if (!isNaN(priceNative) && priceNative > 0) {
        priceUsd = priceNative * ethPrice;
        console.log(`📊 Цена в нативной валюте: ${priceNative}, конвертирована в USD: $${priceUsd.toFixed(6)}`);
      }
    }
    
    if (isNaN(priceUsd) || priceUsd <= 0) {
      console.warn(`⚠️ Некорректная цена токена после парсинга:`, {
        original: priceUsdStr,
        parsed: priceUsd,
        pair: bestPair?.pairAddress
      });
      return null;
    }
    
    // Валидация на разумность
    if (!validateTokenPrice(priceUsd, ethPrice)) {
      console.warn(`⚠️ Цена токена не прошла валидацию: $${priceUsd.toFixed(6)} (максимум: $${Math.max(ethPrice * 10, 10000).toFixed(2)})`);
      return null;
    }
    
    console.log(`✅ Получена валидная цена токена с DexScreener: $${priceUsd.toFixed(6)} (ликвидность: $${parseFloat(bestPair?.liquidity?.usd || '0').toFixed(2)}, пара: ${bestPair?.pairAddress?.substring(0, 10)}...)`);
    return priceUsd;
  } catch (error) {
    console.warn('Failed to fetch token price from DexScreener:', error);
  }

  // Fallback: запрашиваем бэкенд /prices (у него 4 источника: DexScreener, CoinGecko, Uniswap, 1inch)
  try {
    const backendPrices = await apiService.getPrices(tokenAddress);
    if (backendPrices.token_price_usd && backendPrices.token_price_usd > 0 && backendPrices.token_price_usd !== 0.1) {
      console.log(`✅ Получена цена токена с бэкенда: $${backendPrices.token_price_usd.toFixed(6)}`);
      return backendPrices.token_price_usd;
    }
  } catch (error) {
    console.warn('Failed to fetch token price from backend:', error);
  }

  return null;
}

export const useTokenPrice = (tokenAddress?: string): PriceData => {
  const [ethPrice, setEthPrice] = useState<number>(3000);
  const [tokenPrice, setTokenPrice] = useState<number>(0.1);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // Очищаем кеш при смене токена
    if (globalPriceCache && globalPriceCache.tokenAddress !== tokenAddress) {
      globalPriceCache = null;
    }
    
    // Проверяем кеш перед запросом
    const now = Date.now();
    if (globalPriceCache && 
        (now - globalPriceCache.timestamp) < CACHE_TTL &&
        globalPriceCache.tokenAddress === tokenAddress) {
      if (mountedRef.current) {
        setEthPrice(globalPriceCache.ethPrice);
        setTokenPrice(globalPriceCache.tokenPrice);
        setIsLoading(false);
        return;
      }
    }

    const fetchPrices = async () => {
      // Если уже идет запрос, ждем его
      if (fetchPromise) {
        try {
          await fetchPromise;
          if (globalPriceCache && mountedRef.current) {
            setEthPrice(globalPriceCache.ethPrice);
            setTokenPrice(globalPriceCache.tokenPrice);
            setIsLoading(false);
          }
          return;
        } catch (e) {
          // Продолжаем со своим запросом
        }
      }

      try {
        if (mountedRef.current) {
          setIsLoading(true);
          setError(null);
        }

        // Создаем новый промис для кеширования
        fetchPromise = (async () => {
          try {
            // Сначала получаем цену ETH
            const ethPriceValue = await fetchEthPrice();
            
            // Затем получаем цену токена с валидацией относительно ETH
            const tokenPriceValue = tokenAddress 
              ? await fetchTokenPrice(tokenAddress, ethPriceValue)
              : null;

            // Используем полученную цену токена или fallback только если валидация прошла
            let finalTokenPrice = 0.1; // fallback цена
            if (tokenPriceValue !== null && tokenPriceValue > 0) {
              // Дополнительная проверка на разумность
              if (validateTokenPrice(tokenPriceValue, ethPriceValue)) {
                finalTokenPrice = tokenPriceValue;
              } else {
                console.warn(`⚠️ Используется fallback цена токена (${tokenPriceValue} не прошла валидацию)`);
              }
            }

            // Обновляем кеш
            globalPriceCache = {
              ethPrice: ethPriceValue,
              tokenPrice: finalTokenPrice,
              timestamp: Date.now(),
              tokenAddress
            };

            if (mountedRef.current) {
              setEthPrice(ethPriceValue);
              setTokenPrice(finalTokenPrice);
            }
          } catch (err) {
            console.error('Error fetching prices:', err);
            if (mountedRef.current) {
              setError('Failed to fetch prices');
            }
          } finally {
            fetchPromise = null;
            if (mountedRef.current) {
              setIsLoading(false);
            }
          }
        })();

        await fetchPromise;
      } catch (err) {
        fetchPromise = null;
        if (mountedRef.current) {
          console.error('Error in fetchPrices:', err);
          setError('Failed to fetch prices');
          setIsLoading(false);
        }
      }
    };

    // Небольшая задержка при первой загрузке чтобы избежать параллельных запросов
    const timeoutId = setTimeout(fetchPrices, 100);
    
    // Обновляем цены каждые 10 секунд для более актуальных данных
    const interval = setInterval(() => {
      const now = Date.now();
      // Обновляем если кэш отсутствует, устарел, или токен изменился
      if (!globalPriceCache || 
          (now - globalPriceCache.timestamp) >= CACHE_TTL ||
          globalPriceCache.tokenAddress !== tokenAddress) {
        globalPriceCache = null;
        fetchPrices();
      }
    }, 10 * 1000);
    
    return () => {
      clearTimeout(timeoutId);
      clearInterval(interval);
      mountedRef.current = false;
    };
  }, [tokenAddress]);

  return {
    ethPrice,
    tokenPrice,
    isLoading,
    error
  };
};
